import {
  createRef,
  forwardRef,
  Component,
  RefForwardingComponent,
  ReactElement,
  PropsWithChildren,
  RefObject,
  SyntheticEvent,
  ForwardRefExoticComponent,
} from "react";
import throttle from "lodash.throttle";
import cn from "classnames";
import { emitOnScroll, onScrollTo } from "@ebsco-ui/scroll-emitter";
import { getScrollInfo, smoothScroll } from "./utils";

import "./scroll-container.scss";

export interface ScrollContainerProps {
  renderContainer: RefForwardingComponent<
    ReactElement,
    PropsWithChildren<object>
  >;
  className?: string;
}

export interface ScrollContainerState {
  disableScrollAnimation: boolean;
  hasScrollBehavior: boolean;
}

class ScrollContainer extends Component<
  ScrollContainerProps,
  ScrollContainerState
> {
  static defaultProps = {
    renderContainer: (
      props: PropsWithChildren<object>,
      ref: RefObject<HTMLDivElement>,
    ) => <div {...props} ref={ref} />,
  };
  static BASE_CLASS_NAME = "scroll-container";

  private removeOnScrollToListener: (() => void) | undefined;

  private containerRef: RefObject<HTMLElement> = createRef();

  scrollHandler = (event: SyntheticEvent) => {
    emitOnScroll((event.target as HTMLElement).scrollTop);
  };

  onScroll = (event: SyntheticEvent) => {
    event.persist();
    this.throttledScroll(event);
  };

  /**
   * throttle( cb, duration )
   * 100ms provides enough of a throttle without it being noticeable to the end user.
   * @see https://lodash.com/docs/4.17.15#throttle
   */
  throttledScroll: (event: SyntheticEvent) => void = throttle(
    this.scrollHandler,
    100,
  );

  /**
   * @param {number} targetPosition - Y Position to scroll to.
   * @param {number} scrollRate     - Speed of scroll for browsers that do not support scroll behavior.
   */
  scrollTo = ({
    targetPosition,
    scrollRate = 5,
  }: {
    targetPosition: number;
    scrollRate: number;
  }) => {
    const { hasScrollBehavior, disableScrollAnimation } = this.state;
    const scrollContainer = this.containerRef.current;

    if (!scrollContainer) return;

    if (disableScrollAnimation || hasScrollBehavior) {
      scrollContainer.scrollTop = targetPosition;
      return;
    }

    scrollContainer.style.overflow = "hidden";

    smoothScroll(scrollContainer, targetPosition, scrollRate, () => {
      scrollContainer.style.overflow = "auto";
    });
  };

  renderScrollContainer: ForwardRefExoticComponent<any> = forwardRef(
    this.props.renderContainer,
  );

  componentDidMount(): void {
    this.removeOnScrollToListener = onScrollTo(this.scrollTo);
    this.setState(getScrollInfo(this.containerRef.current));
  }

  componentWillUnmount(): void {
    this.removeOnScrollToListener && this.removeOnScrollToListener();
  }

  render() {
    const {
      renderScrollContainer: RenderScrollContainer,
      onScroll,
      containerRef,
    } = this;
    const { className: consumerClassName, children } = this.props;

    return (
      <RenderScrollContainer
        className={cn(ScrollContainer.BASE_CLASS_NAME, consumerClassName)}
        onScroll={onScroll}
        ref={containerRef}
      >
        {children}
      </RenderScrollContainer>
    );
  }
}

export default ScrollContainer;
